import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const COOKIE_KEY = "dealzauber_cookie_consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(COOKIE_KEY);
    if (!saved) {
      // Kurz warten bevor Banner erscheint
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = () => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ consent: "all", date: new Date().toISOString() }));
    setVisible(false);
  };

  const necessary = () => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ consent: "necessary", date: new Date().toISOString() }));
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-[9999] p-3 sm:p-4"
          data-testid="cookie-banner"
        >
          <div className="max-w-5xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden">
            
            {/* Main Banner */}
            <div className="p-4 sm:p-5">
              <div className="flex items-start gap-3">
                
                {/* Icon */}
                <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center mt-0.5">
                  <Cookie className="w-5 h-5 text-[#A259FF]" />
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-outfit font-bold text-slate-900 text-sm">
                      Wir verwenden Cookies
                    </h3>
                    <span className="hidden sm:inline text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                      DSGVO-konform
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Wir nutzen <strong className="text-slate-700">technisch notwendige</strong> Cookies für Login und Voting sowie 
                    externe Dienste (Google Fonts, Affiliate-Partner). Keine Weitergabe an Werbenetze.{" "}
                    <Link to="/datenschutz" className="text-[#A259FF] hover:underline" onClick={() => setVisible(false)}>
                      Datenschutzerklärung
                    </Link>
                  </p>

                  {/* Details ausklappen */}
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="mt-1.5 flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                    data-testid="cookie-details-toggle"
                  >
                    {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    Details {showDetails ? "ausblenden" : "anzeigen"}
                  </button>
                </div>

                {/* Buttons */}
                <div className="flex-shrink-0 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 ml-2">
                  <button
                    onClick={necessary}
                    data-testid="cookie-reject-btn"
                    className="text-xs px-3 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all font-medium whitespace-nowrap"
                  >
                    Nur notwendige
                  </button>
                  <button
                    onClick={accept}
                    data-testid="cookie-accept-btn"
                    className="text-xs px-4 py-2 rounded-xl bg-[#A259FF] hover:bg-[#8B3DFF] text-white font-semibold transition-all whitespace-nowrap shadow-sm shadow-purple-200"
                  >
                    Alle akzeptieren
                  </button>
                </div>
              </div>

              {/* Details */}
              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      
                      <div className="bg-green-50 rounded-xl p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
                          <span className="text-xs font-semibold text-green-800">Notwendig</span>
                          <span className="ml-auto text-xs text-green-600 font-medium">Immer aktiv</span>
                        </div>
                        <ul className="text-xs text-slate-600 space-y-0.5">
                          <li>· Login-Token (7 Tage)</li>
                          <li>· Anonyme Voter-ID</li>
                          <li>· Cookie-Einstellung</li>
                        </ul>
                      </div>

                      <div className="bg-blue-50 rounded-xl p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Cookie className="w-3.5 h-3.5 text-blue-600" />
                          <span className="text-xs font-semibold text-blue-800">Externe Dienste</span>
                        </div>
                        <ul className="text-xs text-slate-600 space-y-0.5">
                          <li>· Google Fonts (Schriften)</li>
                          <li>· Affiliate-Weiterleitungen</li>
                          <li>· Amazon PartnerNet</li>
                        </ul>
                      </div>

                      <div className="bg-slate-50 rounded-xl p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <X className="w-3.5 h-3.5 text-slate-500" />
                          <span className="text-xs font-semibold text-slate-700">Nicht vorhanden</span>
                        </div>
                        <ul className="text-xs text-slate-500 space-y-0.5">
                          <li>· Keine Werbe-Cookies</li>
                          <li>· Kein Google Analytics</li>
                          <li>· Kein Facebook Pixel</li>
                        </ul>
                      </div>

                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
